import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output
} from "@angular/core";
import { SelectionModel } from "@shared/collections";
import { Subject, takeUntil } from "rxjs";

export interface GenericItemChild {
	parentId?: number;
	id: number;
	permissaoEvento: boolean;
	titulo: string;
	eventos?: GenericItemChild[];
}

interface GenericItem {
	id: number;
	titulo: string;
	eventos: GenericItemChild[];
	funcionalidadeMenusFilhos: GenericItemChild[];
}

@Component({
	selector: "app-accordion-checkbox",
	templateUrl: "./accordion-checkbox.component.html",
	styleUrls: ["./accordion-checkbox.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class AccordionCheckboxComponent implements OnInit, OnDestroy {
	@Input() dataSource: GenericItem[] = [];
	@Input() selectionModel!: SelectionModel<{
		parentId?: number;
		id: number;
		permissaoEvento: boolean;
		titulo: string;
	}>;
	@Input() originalValuesUpdate!: Subject<boolean>;
	@Input() accordionBtnPosition: "left" | "right" = "right";
	@Input() disabled = false;

	@Output() nodesToDeleteAndInclude = new EventEmitter<
		GenericItemChild[][]
	>();

	@Output() updateDataSourceInBackground = new EventEmitter<boolean>();

	protected isModuleActive = false;

	private originalSelectedNodes: GenericItemChild[] = [];
	private _onDestroy$ = new Subject<boolean>();

	constructor(private cdRef: ChangeDetectorRef) {}

	ngOnInit(): void {
		this.preCheckNodes();
		this.fillOriginalSelectedNodes();
		this.watchForOriginalValuesUpdate();
	}

	ngOnDestroy(): void {
		this._onDestroy$.next(true);
	}

	fillOriginalSelectedNodes(): void {
		this.originalSelectedNodes = [...this.selectionModel.selected];
	}

	watchForOriginalValuesUpdate(): void {
		if (this.originalValuesUpdate) {
			this.originalValuesUpdate
				.pipe(takeUntil(this._onDestroy$))
				.subscribe(() => {
					this.originalSelectedNodes = [
						...this.selectionModel.selected
					];
					const originalDataSource = [...this.dataSource];
					originalDataSource.forEach((item) => {
						return item.eventos.forEach((event) => {
							if (this.originalSelectedNodes.includes(event)) {
								event.permissaoEvento = true;
								return;
							}
							event.permissaoEvento = false;
						});
					});
					this.dataSource = [...originalDataSource];
					this.isModuleActive =
						this.selectionModel.selected.length > 0;
					this.cdRef.detectChanges();
				});
		}
	}

	preCheckNodesIfModuleIsReports(parentNode: GenericItem): void {
		parentNode.funcionalidadeMenusFilhos.forEach((childNode) => {
			const hasPermission =
				childNode.eventos &&
				childNode.eventos.length > 0 &&
				childNode.eventos[0].permissaoEvento;
			if (hasPermission) {
				this.isModuleActive = true;
				this.selectSingleChildNode(childNode, parentNode.id, false);
			}
		});
	}

	preCheckNodes(): void {
		this.selectionModel.clear();
		this.dataSource.forEach((parentNode) => {
			if (parentNode.eventos.length === 0) {
				this.preCheckNodesIfModuleIsReports(parentNode);
				return;
			}
			parentNode.eventos.forEach((node) => {
				if (node.permissaoEvento) {
					this.isModuleActive = true;
					this.selectSingleChildNode(node, parentNode.id, false);
				}
			});
		});
	}

	decideWhichNodesToIncludeOrRemove(): void {
		const differenceOverTheOriginalSelection =
			this.originalSelectedNodes.filter(
				(originalNode) =>
					!this.selectionModel.selected.includes(originalNode)
			);

		const additionOverTheOriginalSelection =
			this.selectionModel.selected.filter(
				(newNode) => !this.originalSelectedNodes.includes(newNode)
			);

		this.nodesToDeleteAndInclude.emit([
			differenceOverTheOriginalSelection,
			additionOverTheOriginalSelection
		]);
	}

	selectAllNodesPerModule(element: HTMLInputElement): void {
		this.dataSource.forEach((node) => {
			const descendants = this.getDescendants(node);
			descendants.forEach((childNode) => {
				childNode.parentId = node.id;
			});
			if (element.checked) {
				this.selectionModel.select(true, ...descendants);
				this.decideWhichNodesToIncludeOrRemove();
				return;
			}
			this.selectionModel.unselect(true, ...descendants);
			this.decideWhichNodesToIncludeOrRemove();
		});
	}

	getDescendants(node: GenericItem): GenericItemChild[] {
		if (node.eventos.length === 0) {
			return node.funcionalidadeMenusFilhos;
		}
		return node.eventos;
	}

	chooseBetweenEventOrFunctionalityChildren(
		node: GenericItem
	): GenericItemChild[] {
		if (node.eventos.length === 0) {
			return [...node.funcionalidadeMenusFilhos];
		}
		return [...node.eventos];
	}

	selectEntireNode(node: GenericItem): void {
		const childrenNodes =
			this.chooseBetweenEventOrFunctionalityChildren(node);
		const descendants = this.getDescendants(node);
		descendants.forEach((childNode) => {
			childNode.parentId = node.id;
		});
		const isAllChildrenNodesSelected = childrenNodes.every((node) =>
			this.selectionModel.selected.includes(node)
		);

		if (isAllChildrenNodesSelected) {
			this.selectionModel.unselect(true, ...descendants);
			this.decideWhichNodesToIncludeOrRemove();
			return;
		}
		this.selectionModel.select(true, ...descendants);
		this.decideWhichNodesToIncludeOrRemove();
	}

	selectSingleChildNode(
		node: GenericItemChild,
		parentNodeId: number,
		markAsDirty = true
	): void {
		node.parentId = parentNodeId;
		this.selectionModel.toggle(markAsDirty, node);
		this.decideWhichNodesToIncludeOrRemove();
	}

	isSingleNodeSelected(node: GenericItemChild): boolean {
		return this.selectionModel.isNodeAlreadySelected(node);
	}

	descendantsAllSelected(node: GenericItem): boolean {
		const descendants = this.getDescendants(node);
		const result = descendants.every((node) =>
			this.selectionModel.isNodeAlreadySelected(node)
		);
		return result;
	}

	descendantsPartiallySelected(node: GenericItem): boolean {
		const descendants = this.getDescendants(node);
		const result = descendants.some((node) =>
			this.selectionModel.isNodeAlreadySelected(node)
		);
		return result && !this.descendantsAllSelected(node);
	}

	sliceNodeEvents(nodes: GenericItemChild[]): GenericItemChild[][] {
		const sizeLimitSingleColumn = 6;
		const numberOfColumns = 2;
		if (nodes.length < sizeLimitSingleColumn) {
			return [nodes, []];
		}
		const startIndexArray = Math.ceil(nodes.length / numberOfColumns);
		return [nodes.slice(0, startIndexArray), nodes.slice(startIndexArray)];
	}

	get isAllNodesPerModuleChecked(): boolean {
		const numberOfEvents = this.dataSource.reduce(
			(accumulator, currentNode) => {
				if (currentNode.eventos.length === 0) {
					return (
						accumulator +
						currentNode.funcionalidadeMenusFilhos.length
					);
				}
				return accumulator + currentNode.eventos.length;
			},
			0
		);
		return this.selectionModel.selected.length === numberOfEvents;
	}
}
